import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FiMenu, FiPlus, FiEdit, FiTrash2, FiSearch, FiEye, FiBook } from 'react-icons/fi';
import { BsYoutube } from 'react-icons/bs';
import AdminSidebar from './AdminSidebar';
import { getAllSermons, deleteSermon } from '../../Redux/slices/sermonSlice';

const SermonList = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dispatch = useDispatch();
  const { sermons, loading } = useSelector(state => state.sermons);

  useEffect(() => {
    dispatch(getAllSermons({ limit: 100 }));
  }, [dispatch]);


  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this sermon?')) {
      await dispatch(deleteSermon(id));
      dispatch(getAllSermons({ limit: 100 }));
    }
  };

  const filteredSermons = sermons.filter(sermon =>
    sermon.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sermon.preacher.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sermon.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} currentPage="sermons" />

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white shadow-sm">
          <div className="flex items-center justify-between p-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden">
              <FiMenu size={24} />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">Sermon Management</h2>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-800">Admin User</p>
                <p className="text-xs text-gray-500">admin@houseofpraise.com</p>
              </div>
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                A
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-6">
          {/* Header Actions */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search sermons..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Add Button */}
              <Link
                to="/admin/sermons/add"
                className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition"
              >
                <FiPlus size={20} />
                <span>Add New Sermon</span>
              </Link>
            </div>
          </div>

          {/* Sermons Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredSermons.map((sermon) => (
                <div key={sermon._id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition overflow-hidden">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{sermon.title}</h3>
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">Preacher:</span> {sermon.preacher}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Date:</span> {new Date(sermon.date).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                      {sermon.category}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {sermon.description}
                  </p>

                  {/* YouTube Link */}
                  <div className="flex items-center space-x-2 mb-4 p-3 bg-red-50 rounded-lg">
                    <BsYoutube className="text-red-600" size={20} />
                    <a
                      href={sermon.youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-red-600 hover:text-red-700 font-medium truncate"
                    >
                      {sermon.youtubeUrl}
                    </a>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center space-x-4 mb-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <FiEye size={16} />
                      <span>{sermon.views} views</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
                    <Link
                      to={`/admin/sermons/edit/${sermon._id}`}
                      className="flex-1 flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition"
                    >
                      <FiEdit size={16} />
                      <span>Edit</span>
                    </Link>
                    <button
                      onClick={() => handleDelete(sermon._id)}
                      className="flex-1 flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition"
                    >
                      <FiTrash2 size={16} />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {filteredSermons.length === 0 && (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
              <FiBook className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No Sermons Found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your first sermon'}
              </p>
              {!searchTerm && (
                <Link
                  to="/admin/sermons/add"
                  className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition"
                >
                  <FiPlus size={20} />
                  <span>Add New Sermon</span>
                </Link>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default SermonList;
